import queryString from "query-string";
import type { CostCenter, PaginatedResource } from "~/application/types";
import { api } from "~/infrastructure/api";
import { CostCenterDTO } from "~/infrastructure/api/dtos";
import { mapCostCenterDTO } from "~/infrastructure/api/mappers";
import {
  type ICostCenterService,
  type IFindCostCenterParams,
  type IFindCostCenterWithoutApprovalModelLinkParams,
  FindCostCenterState,
} from "./ICostCenterService";

export class CostCenterService implements ICostCenterService {
  async findById(id: string): Promise<CostCenter> {
    return await api
      .get<CostCenterDTO>(`/customers/cost-centers/${id}`)
      .then(({ data }) => mapCostCenterDTO(data));
  }

  async find({
    customerId,
    name,
    page,
    state,
    phase,
    hasIssuedOrder,
    headquartersOnly,
    branchIds,
    modelTypes,
    customerEmployeeIds,
    context,
  }: IFindCostCenterParams): Promise<CostCenter[]> {
    const url = queryString.stringifyUrl(
      {
        url: `/customers/${customerId}/cost-centers`,
        query: {
          name,
          page,
          state,
          phase,
          has_issued_order: hasIssuedOrder,
          headquarters_only: headquartersOnly,
          branches_uuids: branchIds,
          model_types: modelTypes,
          customer_employee_uuids: customerEmployeeIds,
          context,
        },
      },
      {
        arrayFormat: "bracket",
        skipNull: true,
      },
    );

    return await api
      .get<PaginatedResource<CostCenterDTO[]>>(url)
      .then(({ data: response }) => response.data.map(mapCostCenterDTO));
  }

  async findPaginated({
    customerId,
    name,
    page,
    filterByCostCenter,
    withApprovalModels,
    branchIds,
    headquartersOnly,
    modelTypes,
    customerEmployeeIds,
    context,
    state = FindCostCenterState.ALL,
  }: IFindCostCenterParams): Promise<PaginatedResource<CostCenter[]>> {
    const url = queryString.stringifyUrl(
      {
        url: `/customers/${customerId}/cost-centers`,
        query: {
          name,
          page,
          state,
          with_approval_model: filterByCostCenter,
          model_types: modelTypes,
          with_approval_models: withApprovalModels,
          headquarters_only: headquartersOnly,
          branches_uuids: headquartersOnly ? undefined : branchIds,
          customer_employee_uuids: customerEmployeeIds,
          context,
        },
      },
      {
        arrayFormat: "bracket",
        skipNull: true,
      },
    );

    return await api.get<PaginatedResource<CostCenterDTO[]>>(url).then(({ data: response }) => ({
      data: response.data.map(mapCostCenterDTO),
      meta: response.meta,
      links: response.links,
    }));
  }

  async findWithoutApprovalModelLink({
    name,
    customerId,
    approvalModelId,
    headquartersOnly,
    branchIds,
    modelTypes,
    isActive,
  }: IFindCostCenterWithoutApprovalModelLinkParams): Promise<CostCenter[]> {
    const url = queryString.stringifyUrl(
      {
        url: `/customers/${customerId}/approval-models/cost-centers`,
        query: {
          approval_model_uuid: approvalModelId,
          name,
          headquarters_only: headquartersOnly,
          branches_uuids: headquartersOnly ? undefined : branchIds,
          model_types: modelTypes,
          active: isActive,
        },
      },
      {
        arrayFormat: "bracket",
        skipNull: true,
      },
    );

    return await api
      .get<{ data: CostCenterDTO[] }>(url)
      .then(({ data }) => data.data.map(mapCostCenterDTO));
  }

  async create(
    data: Omit<CostCenter, "uuid"> & { markAllBranches?: boolean },
  ): Promise<CostCenter> {
    return await api
      .post<{ data: CostCenterDTO }>("/customers/cost-centers", {
        name: data.name,
        cost_center_code: data.costCenterCode,
        customer_uuid: data.customerId,
        belongs_to_headquarters: data.belongsToHeadquarters,
        branches_uuids: data.branches?.map((b) => b.uuid),
        mark_all_branches: data.markAllBranches,
      })
      .then(({ data }) => mapCostCenterDTO(data.data));
  }

  async updateById(data: CostCenter & { markAllBranches?: boolean }): Promise<CostCenter> {
    return await api
      .put<CostCenterDTO>(`/customers/cost-centers/${data.uuid}`, {
        name: data.name,
        cost_center_code: data.costCenterCode,
        customer_uuid: data.customerId,
        belongs_to_headquarters: data.belongsToHeadquarters,
        branches_uuids: data.branches?.map((b) => b.uuid),
        mark_all_branches: data.markAllBranches,
      })
      .then(({ data }) => mapCostCenterDTO(data));
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async toggleActive(data: CostCenter): Promise<void> {
    return await api
      .patch<undefined>(
        `/customers/cost-centers/${data.uuid}/${data.isActive ? "inactive" : "active"}`,
      )
      .then(({ data }) => data);
  }

  async deleteById(data: CostCenter): Promise<void> {
    return await api
      .delete<undefined>(`/customers/cost-centers/${data.uuid}`)
      .then(({ data }) => data);
  }
}
